import { Edit2, Eye, EyeOff, MapPin, Plus, Save, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosHandler';

const ICBCLocationManagement = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);

    const [formData, setFormData] = useState({
        key: '',
        name: '',
        address: '',
        displayOrder: 0,
    });

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await axios.get('/api/icbc/admin/locations');
            if (response.data.success) {
                setLocations(response.data.locations);
            }
        } catch (error) {
            console.error('Error fetching ICBC locations:', error);
            alert('Error fetching ICBC locations');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            key: '',
            name: '',
            address: '',
            displayOrder: 0,
        });
        setShowAddForm(false);
        setEditingLocation(null);
    };

    const handleAddLocation = async (e) => {
        e.preventDefault();

        if (!formData.key || !formData.name || !formData.address) {
            alert('Please fill in all required fields');
            return;
        }

        setSaving(true);

        try {
            const response = await axios.post('/api/icbc/admin/locations', formData);
            if (response.data.success) {
                await fetchLocations();
                resetForm();
                alert('ICBC location added successfully!');
            }
        } catch (error) {
            console.error('Error adding location:', error);
            alert(error.response?.data?.error || 'Error adding ICBC location');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateLocation = async (e) => {
        e.preventDefault();

        if (!editingLocation) return;

        setSaving(true);

        try {
            const response = await axios.put(`/api/icbc/admin/locations/${editingLocation._id}`, {
                name: editingLocation.name,
                address: editingLocation.address,
                displayOrder: editingLocation.displayOrder,
                isActive: editingLocation.isActive,
            });

            if (response.data.success) {
                await fetchLocations();
                setEditingLocation(null);
                alert('ICBC location updated successfully!');
            }
        } catch (error) {
            console.error('Error updating location:', error);
            alert(error.response?.data?.error || 'Error updating ICBC location');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteLocation = async (locationId) => {
        if (!window.confirm('Are you sure you want to delete this ICBC location?')) {
            return;
        }

        try {
            const response = await axios.delete(`/api/icbc/admin/locations/${locationId}`);
            if (response.data.success) {
                await fetchLocations();
                alert('ICBC location deleted successfully!');
            }
        } catch (error) {
            console.error('Error deleting location:', error);
            alert(error.response?.data?.error || 'Error deleting ICBC location');
        }
    };

    const toggleActive = async (location) => {
        try {
            const response = await axios.put(`/api/icbc/admin/locations/${location._id}`, {
                isActive: !location.isActive,
            });

            if (response.data.success) {
                await fetchLocations();
            }
        } catch (error) {
            console.error('Error toggling location status:', error);
            alert('Error updating location status');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">ICBC Location Management</h1>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
                >
                    {showAddForm ? (
                        <>
                            <X className="w-4 h-4" />
                            Cancel
                        </>
                    ) : (
                        <>
                            <Plus className="w-4 h-4" />
                            Add New Location
                        </>
                    )}
                </button>
            </div>

            {/* Add New Location Form */}
            {showAddForm && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                    <h2 className="text-xl font-semibold mb-4">Add New ICBC Location</h2>
                    <form onSubmit={handleAddLocation}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Key (unique identifier) *
                                </label>
                                <input
                                    type="text"
                                    value={formData.key}
                                    onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., burnaby-main"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">Used for identification. Use lowercase with hyphens.</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Location Name *
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Burnaby - Main Street"
                                    required
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Address *
                                </label>
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., 4211 Kingsway, Burnaby, BC"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Display Order
                                </label>
                                <input
                                    type="number"
                                    value={formData.displayOrder}
                                    onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    min="0"
                                />
                                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2"
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Plus className="w-4 h-4" />
                                    Add Location
                                </>
                            )}
                        </button>
                    </form>
                </div>
            )}

            {/* Locations List */}
            <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 border-b">
                    <h2 className="text-lg font-semibold">ICBC Locations ({locations.length})</h2>
                </div>

                <div className="divide-y">
                    {locations.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                            <p>No ICBC locations yet. Add your first location above!</p>
                        </div>
                    ) : (
                        locations.map((location) => (
                            <div key={location._id} className="p-4 hover:bg-gray-50">
                                {editingLocation?._id === location._id ? (
                                    // Edit Mode
                                    <form onSubmit={handleUpdateLocation} className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Key (read-only)</label>
                                                <input
                                                    type="text"
                                                    value={editingLocation.key}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                                                <input
                                                    type="text"
                                                    value={editingLocation.name}
                                                    onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Location Name"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
                                                <input
                                                    type="text"
                                                    value={editingLocation.address}
                                                    onChange={(e) => setEditingLocation({ ...editingLocation, address: e.target.value })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    placeholder="Address"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 mb-1">Display Order</label>
                                                <input
                                                    type="number"
                                                    value={editingLocation.displayOrder}
                                                    onChange={(e) => setEditingLocation({ ...editingLocation, displayOrder: parseInt(e.target.value) || 0 })}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="submit"
                                                disabled={saving}
                                                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 disabled:bg-gray-400 flex items-center gap-2"
                                            >
                                                <Save className="w-4 h-4" />
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingLocation(null)}
                                                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center gap-2"
                                            >
                                                <X className="w-4 h-4" />
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    // View Mode
                                    <div className="flex items-start gap-4">
                                        {/* Icon */}
                                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-6 h-6 text-blue-600" />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between">
                                                <div>
                                                    <h3 className="font-semibold text-lg">{location.name}</h3>
                                                    <p className="text-gray-600 text-sm mt-1">{location.address}</p>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                                        <span className="font-mono bg-gray-100 px-2 py-1 rounded">Key: {location.key}</span>
                                                        <span>Order: {location.displayOrder}</span>
                                                        <span className={`px-2 py-1 rounded ${location.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                            {location.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => toggleActive(location)}
                                                        className={`p-2 rounded-lg ${location.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}
                                                        title={location.isActive ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {location.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingLocation({ ...location })}
                                                        className="p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                                                        title="Edit"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteLocation(location._id)}
                                                        className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default ICBCLocationManagement;

